import { Channel } from 'pusher-js';
import { captureMessage } from '@sentry/react';
import { aiLog } from 'src/utils/logger';
import { getPusher, onPusherReconnect, unsubscribeChannel } from './pusher';

// AI session stream over the shared Reverb singleton. One session = one private channel. The
// broadcast is a thin signal (no parcel in the payload), so the listener re-fetches the session.

interface AiSessionUpdatedEvent {
  uxId: string;
  status: string;
  step?: string | null;
}

const activeSubscriptions = new Map<string, Channel>();

/** Subscribe to a session's update signals (idempotent per uxId). Returns an unsubscribe fn. */
export function subscribeToAiSession(
  uxId: string,
  onSignal: (e: { status: string; step?: string }) => void,
): () => void {
  if (activeSubscriptions.has(uxId)) return () => unsubscribe(uxId);

  const pusher = getPusher();
  if (!pusher) return () => {};

  const channelName = `private-ai-session.${uxId}`;
  aiLog.info('· subscribing', channelName);
  const channel = pusher.subscribe(channelName);
  activeSubscriptions.set(uxId, channel);

  channel.bind('pusher:subscription_succeeded', () => {
    aiLog.info('· subscribed', channelName);
  });

  channel.bind('pusher:subscription_error', (err: { status?: number } | unknown) => {
    const status = (err as { status?: number })?.status;
    aiLog.warn(`ai-session subscribe failed for ${channelName}`, status ?? err);
    captureMessage('ai session subscription failed', {
      level: 'warning',
      tags: { context: 'ai_session_socket', http_status: String(status ?? 'unknown') },
      extra: { channel: channelName }, // private-ai-session.<uxId> — an id, not PII
    });
    unsubscribe(uxId);
  });

  channel.bind('ai-session.updated', (e: AiSessionUpdatedEvent) => {
    aiLog.info('⚡ ai-session.updated', e);
    onSignal({ status: e.status, step: e.step ?? undefined });
  });

  // Reverb doesn't buffer events across a transient drop, so a rung that completes while the socket
  // is down would be missed forever — refetch on reconnect to recover (mirrors exportSocket).
  const offReconnect = onPusherReconnect(() => {
    aiLog.info('· reconnect → refetch', channelName);
    onSignal({ status: 'reconnect' });
  });

  return () => {
    offReconnect();
    unsubscribe(uxId);
  };
}

function unsubscribe(uxId: string): void {
  if (!activeSubscriptions.has(uxId)) return;
  activeSubscriptions.delete(uxId);
  unsubscribeChannel(`private-ai-session.${uxId}`);
}
